import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetNotificationSettingFieldsQuery } from '@/middlewares/defaultApi';
import type { RootState } from '@/reducers/reducers';
import { selectAppSettings, selectIsDemoInstanceOrDemoModeEnabled } from '@/selectors/selectors';
import type { SettingField } from '@/types';

// Demo apps aren't stored, so the endpoint can't serve them; no extra fields beats a hand-copied list
const DEMO_SETTING_FIELDS: Record<string, SettingField[]> = {};

// Fields are keyed by notification id; notifications with nothing beyond channels and mentions have none
export const useNotificationSettingFields = (
    orgId: string,
    appId: string,
): {
    settingFieldsByNotificationId: Record<string, SettingField[]> | undefined;
    isLoadingSettingFields: boolean;
    isSettingFieldsError: boolean;
} => {
    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, orgId));
    const { data, isFetching, isError, refetch } = useGetNotificationSettingFieldsQuery(
        { orgId, appId },
        { refetchOnMountOrArgChange: false, skip: isDemoMode },
    );

    // Options and availability depend on the app's integrations, so refetch when they change
    const integrations = useSelector((state: RootState) => selectAppSettings(state, appId)?.integrations);
    const previousIntegrations = useRef(integrations);
    useEffect(() => {
        if (isDemoMode || isEqual(previousIntegrations.current, integrations)) {
            return;
        }
        previousIntegrations.current = integrations;
        refetch();
    }, [integrations, isDemoMode, refetch]);

    return {
        settingFieldsByNotificationId: isDemoMode ? DEMO_SETTING_FIELDS : data?.data,
        isLoadingSettingFields: isFetching,
        isSettingFieldsError: isError,
    };
};
